<style scoped>
  .ai-history-message.card {
    margin-bottom: 1rem;
    border-left: 3px solid #e7eaec;
  }

  .ai-history-message.user {
    border-left-color: #1ab394;
  }

  .ai-history-message.assistant {
    border-left-color: #1c84c6;
  }

  .ai-history-content {
    white-space: pre-wrap;
    word-wrap: break-word;
    margin-bottom: 0;
  }

  .ai-history-message details {
    margin-top: 0.5rem;
  }

  .ai-history-message summary {
    cursor: pointer;
    font-size: 0.8rem;
    color: #676a6c;
  }

  .ai-history-message pre {
    margin: 0.5rem 0 0;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 4px;
    font-size: 0.75rem;
    max-height: 400px;
    overflow: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
</style>

<template>
  <div>
    <app-header>
      <template #title>
        <h1 class="d-inline me-4">{{ conversation.title || "Conversation" }}</h1>
      </template>
    </app-header>

    <div class="container my-5">
      <div class="card mb-4" v-if="conversation.id">
        <div class="card-body">
          <div class="row">
            <div class="col-6 col-lg-3">
              <p class="data-label mb-0">Agent</p>
              <p class="strong mb-0">{{ conversation.agent }}</p>
            </div>
            <div class="col-6 col-lg-3">
              <p class="data-label mb-0">User</p>
              <p class="strong mb-0">{{ conversation.user_name }}</p>
            </div>
            <div class="col-6 col-lg-1">
              <p class="data-label mb-0">Messages</p>
              <p class="strong mb-0">{{ conversation.messages_count }}</p>
            </div>
            <div class="col-6 col-lg-2">
              <p class="data-label mb-0">Started</p>
              <p class="strong mb-0">
                {{ conversation.created_at | moment("DD/MM/YYYY HH:mm:ss") }}
              </p>
            </div>
            <div class="col-6 col-lg-3" v-if="conversation.context">
              <p class="data-label mb-0">Context</p>
              <p class="strong mb-0 text-truncate">
                <router-link :to="contextRoute">
                  <span class="text-capitalize">{{ conversation.context.type }}</span>
                  #{{ conversation.context.id }} {{ conversation.context.name }}
                </router-link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-4" v-if="delegatedFrom || delegated.length">
        <div class="card-body">
          <p class="data-label mb-2" v-if="delegatedFrom">Delegated run</p>
          <p class="mb-0" v-if="delegatedFrom">
            This conversation was delegated by
            <router-link
              :to="{ name: 'settings.ai-history.show', params: { conversation_id: delegatedFrom } }"
            >
              its parent conversation
            </router-link>.
          </p>

          <template v-if="delegated.length">
            <p class="data-label mb-2" :class="{ 'mt-3': delegatedFrom }">
              Delegated to other agents ({{ delegated.length }})
            </p>
            <ul class="mb-0">
              <li v-for="run in delegated" :key="run.id">
                <router-link
                  :to="{ name: 'settings.ai-history.show', params: { conversation_id: run.id } }"
                >
                  {{ run.agent }}: {{ run.title || run.id }}
                </router-link>
                <span class="text-muted">({{ run.messages_count }} messages)</span>
              </li>
            </ul>
          </template>
        </div>
      </div>

      <div
        v-bind:key="message.id"
        v-for="message in messages"
        class="card ai-history-message"
        :class="message.role"
      >
        <div class="card-body">
          <div class="row mb-2">
            <div class="col">
              <span class="badge" :class="message.role === 'user' ? 'bg-primary' : 'bg-info'">
                {{ roleLabel(message.role) }}
              </span>
            </div>
            <div class="col-auto text-muted">
              {{ message.created_at | moment("DD/MM/YYYY HH:mm:ss") }}
            </div>
          </div>

          <p class="ai-history-content">{{ message.content || "(no text content)" }}</p>

          <details v-if="message.tool_calls && message.tool_calls.length">
            <summary>Tool calls ({{ message.tool_calls.length }})</summary>
            <pre>{{ pretty(message.tool_calls) }}</pre>
          </details>

          <details v-if="message.tool_results && message.tool_results.length">
            <summary>Tool results ({{ message.tool_results.length }})</summary>
            <pre>{{ pretty(message.tool_results) }}</pre>
          </details>

          <details v-if="message.attachments && message.attachments.length">
            <summary>Attachments ({{ message.attachments.length }})</summary>
            <pre>{{ pretty(message.attachments) }}</pre>
          </details>

          <details v-if="hasData(message.usage)">
            <summary>Usage</summary>
            <pre>{{ pretty(message.usage) }}</pre>
          </details>

          <details v-if="hasData(message.meta)">
            <summary>Meta</summary>
            <pre>{{ pretty(message.meta) }}</pre>
          </details>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { http } from "utils";

export default {
  data: function () {
    return {
      conversation: {},
      messages: [],
      delegated: [],
      delegatedFrom: null,
    };
  },

  created: function () {
    this.load();
  },

  computed: {
    contextRoute() {
      if (this.conversation.context.type === "project") {
        return {
          name: "projects.show.details",
          params: { project_id: this.conversation.context.id },
        };
      }

      return {
        name: "opportunities.overview",
        params: { opportunity_id: this.conversation.context.id },
      };
    },
  },

  watch: {
    // Navigating between linked conversations only changes the route
    // param, so the component is reused and created() does not re-run.
    "$route.params.conversation_id": function () {
      this.load();
    },
  },

  methods: {
    async load() {
      const { data } = await http.get(`ai/history/${this.$route.params.conversation_id}`);

      this.conversation = data.data;
      this.messages = data.messages;
      this.delegated = data.delegated || [];
      this.delegatedFrom = data.delegated_from || null;
    },

    // On a delegated run the "user" is the agent that delegated the task,
    // not a person.
    roleLabel(role) {
      return role === "user" && this.delegatedFrom ? "agent" : role;
    },

    pretty(value) {
      return JSON.stringify(value, null, 2);
    },

    hasData(value) {
      return value && Object.keys(value).length;
    },
  },
};
</script>
